import { uploadToGCS, uploadLogoToGCS } from '@/lib/storage';
import { nanoid } from 'nanoid';
import { z } from 'zod';

// zod validation for file that image, required, and max 5 MB - allow only JPG, and PNG
const FILE_SCHEMA = z.object({
  name: z.string(),
  size: z
    .number()
    .int()
    .min(1)
    .max(5 * 1024 * 1024),
  type: z.string().regex(/image\/(jpeg|png)/),
});

// Asset types that live in the logos bucket (headtotoe-dev-app-assets)
const LOGO_BUCKET_PREFIXES = ['logos/web', 'logos/hub'];

/**
 * Upload asset to GCS.
 *
 * Assets under logos/ (web or hub) are uploaded to the logos bucket via the
 * dedicated /actions/upload-logo endpoint. All other assets go to the customer
 * assets bucket via /actions/upload.
 *
 * @param {File} file - The file object to upload
 * @param {string} [assetType='asset'] - Type/path prefix for the asset
 * @param {string} [customName] - Optional custom file name (without extension)
 * @return {Promise<{url: string, path: string, assetId?: string}>}
 */
export const uploadAsset = async (file, assetType = 'asset', customName = null) => {
  FILE_SCHEMA.parse(file);

  if (LOGO_BUCKET_PREFIXES.includes(assetType)) {
    // Extract subfolder from e.g. 'logos/web' → 'web'
    const subfolder = assetType.split('/')[1];
    // Pass customName as fileName so the hub logo can reuse the org logo filename
    const { fileName } = await uploadLogoToGCS({
      file,
      subfolder,
      ...(customName && { fileName: customName }),
    });
    return {
      url: fileName,
      path: `${assetType}/${fileName}`,
    };
  }

  const ext = file.name.split('.').pop();
  const name = customName || `${nanoid()}.${ext}`;
  const result = await uploadToGCS({ file, name });

  return {
    url: name,
    path: `${assetType}/${name}`,
    assetId: result.assetId,
  };
};
